/**
 * Feb 2026 · Packing Slip Backfill Report (read-only).
 *
 * Diagnostic view: for every slip in a chosen range, propose the most-
 * likely Sales Order by matching box.product_code (and customer when the
 * slip is already linked). NEVER mutates — the user approves the report,
 * then a separate backfill action would apply the linkage.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ClipboardTextIcon, ArrowsClockwiseIcon, DownloadSimpleIcon,
  MagnifyingGlassIcon, ArrowLeftIcon, WarningIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";

const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";

const statusCls = (s) => {
  if (s === "Already Linked") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (s === "Single Match") return "bg-sky-500/15 text-sky-300 border-sky-500/30";
  if (s === "Multiple Matches Found") return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (s === "No Match Found") return "bg-red-500/15 text-red-300 border-red-500/30";
  if ((s || "").startsWith("Linked ·")) return "bg-red-500/15 text-red-300 border-red-500/40";
  return "bg-zinc-500/15 text-zinc-400 border-zinc-500/30";
};

export default function PackingSlipBackfillReport() {
  const [from_, setFrom] = useState("PS-26-001");
  const [to_, setTo] = useState("PS-26-286");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  // Feb 2026 · Apply-Backfill state · per-slip selections for auto and
  // manual rows. Multi-Match rows require the user to pick the SO id;
  // Single-Match rows auto-pick the first candidate.
  const [selections, setSelections] = useState({}); // slip_no → to_order_id
  const [applying, setApplying] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/packing-slips/backfill-report", {
        params: { from_slip: from_, to_slip: to_ },
      });
      setData(data);
      // Pre-select the single candidate on Single-Match rows so "Apply
      // Selected" doesn't force the operator to tick each one.
      const seed = {};
      (data.rows || []).forEach(r => {
        if (r.status === "Single Match" && r.candidates.length === 1) {
          seed[r.slip_no] = r.candidates[0].id;
        }
      });
      setSelections(seed);
      toast.success(`Scanned ${data.stats.total} slip(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Report failed");
    } finally { setLoading(false); }
  };

  const loadLog = async () => {
    try {
      const { data } = await api.get("/packing-slips/backfill-log", { params: { limit: 100 } });
      setAuditLog(data || []);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { run(); loadLog(); /* eslint-disable-next-line */ }, []);

  const applyBackfill = async () => {
    const rows = (data?.rows || []).filter(r => selections[r.slip_no]);
    if (!rows.length) { toast.error("Pick at least one row to apply"); return; }
    if (!confirm(`Apply ${rows.length} backfill(s)? Each slip's order_id will be updated and audit-logged.`)) return;
    setApplying(true);
    try {
      const payload = {
        slips: rows.map(r => ({
          slip_no: r.slip_no,
          to_order_id: selections[r.slip_no],
          source: r.status === "Single Match" ? "auto-single" : "manual",
        })),
      };
      const { data: res } = await api.post("/packing-slips/apply-backfill", payload);
      toast.success(`Applied ${res.applied_count} · Failed ${res.failed_count}`);
      if ((res.failed || []).length) {
        console.warn("Backfill failures:", res.failed);
        toast.info(`${res.failed_count} row(s) failed — see console`);
      }
      setSelections({});
      run(); loadLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  const filtered = useMemo(() => {
    const rows = data?.rows || [];
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!needle) return true;
      return [r.slip_no, r.product_code, r.customer, r.proposed_so_no, r.current_so_no, r.status]
        .filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [data, q, statusFilter]);

  const stats = data?.stats || {};

  const exportCsv = () => {
    exportToCSV("packing-slip-backfill-report.csv", filtered.map(r => ({
      slip_no: r.slip_no, pack_date: (r.pack_date || "").slice(0, 10),
      product_code: r.product_code, box_count: r.box_count,
      customer: r.customer, current_so: r.current_so_no,
      proposed_so: r.proposed_so_no, status: r.status,
      match_count: r.match_count,
    })), [
      { key: "slip_no", label: "Slip No" }, { key: "pack_date", label: "Pack Date" },
      { key: "product_code", label: "Product Code" }, { key: "box_count", label: "Boxes" },
      { key: "customer", label: "Customer" }, { key: "current_so", label: "Current SO" },
      { key: "proposed_so", label: "Proposed SO No" }, { key: "status", label: "Status" },
      { key: "match_count", label: "Match Count" },
    ]);
  };

  return (
    <div className="p-6 space-y-5" data-testid="ps-backfill-report">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Link to="/" className="hover:text-white flex items-center gap-1">
              <ArrowLeftIcon size={10} weight="bold" />Dashboard
            </Link>
            <span>·</span><span>Reports · Packing</span>
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-3">
            <ClipboardTextIcon size={26} weight="bold" className="text-[var(--accent)]" />
            Packing Slip Backfill Report
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Read-only proposal · matches each slip's box <span className="font-mono text-zinc-300">product_code</span> (and customer when linked) against every Sales Order. No database changes are made — approve the report first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ps-report-refresh" onClick={run} disabled={loading}
            className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
            <ArrowsClockwiseIcon size={13} weight="bold" />{loading ? "Scanning…" : "Run Report"}
          </button>
          <button data-testid="ps-report-apply" onClick={applyBackfill}
            disabled={applying || Object.keys(selections).length === 0}
            className="h-10 px-4 bg-emerald-600 hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40"
            title="Update the order_id on every ticked slip · every mutation is audit-logged">
            <WarningIcon size={13} weight="bold" />
            {applying ? "Applying…" : `Apply Selected · ${Object.keys(selections).length}`}
          </button>
          <button data-testid="ps-report-log" onClick={() => { setShowLog(v => !v); if (!showLog) loadLog(); }}
            className="h-10 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2">
            <ClipboardTextIcon size={13} weight="bold" />Audit Log · {auditLog.length}
          </button>
          <button data-testid="ps-report-export" onClick={exportCsv} disabled={!filtered.length}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2 disabled:opacity-40">
            <DownloadSimpleIcon size={13} weight="bold" />Export CSV
          </button>
        </div>
      </div>

      {/* Range picker */}
      <div className="tactical-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">From Slip No</label>
          <input data-testid="ps-range-from" value={from_} onChange={e => setFrom(e.target.value)}
            className={`${inpCls} w-48 font-mono`} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">To Slip No</label>
          <input data-testid="ps-range-to" value={to_} onChange={e => setTo(e.target.value)}
            className={`${inpCls} w-48 font-mono`} />
        </div>
        <button onClick={run} disabled={loading}
          className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">
          Apply Range
        </button>
        {data?.generated_at && (
          <span className="ml-auto text-[10px] text-zinc-500 font-mono">Generated {(data.generated_at || "").slice(0, 19).replace("T", " ")}</span>
        )}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="tactical-card p-4"><div className="overline mb-1">Scanned</div><div className="font-mono text-3xl font-semibold text-white" data-testid="stat-total">{stats.total || 0}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-emerald-500 cursor-pointer" onClick={() => setStatusFilter("Already Linked")}><div className="overline text-emerald-300 mb-1">Already Linked</div><div className="font-mono text-3xl font-semibold text-emerald-400" data-testid="stat-linked">{stats.already_linked_ok || 0}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-sky-500 cursor-pointer" onClick={() => setStatusFilter("Single Match")}><div className="overline text-sky-300 mb-1">Single Match</div><div className="font-mono text-3xl font-semibold text-sky-400" data-testid="stat-single">{stats.single_match || 0}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-amber-500 cursor-pointer" onClick={() => setStatusFilter("Multiple Matches Found")}><div className="overline text-amber-300 mb-1">Multiple Matches</div><div className="font-mono text-3xl font-semibold text-amber-400" data-testid="stat-multi">{stats.multiple_matches || 0}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-red-500 cursor-pointer" onClick={() => setStatusFilter("No Match Found")}><div className="overline text-red-300 mb-1">No Match</div><div className="font-mono text-3xl font-semibold text-red-400" data-testid="stat-none">{stats.no_match || 0}</div></div>
      </div>

      {(stats.linked_mismatch || 0) > 0 && (
        <div className="tactical-card p-3 border-l-2 border-l-red-500 flex items-center gap-2 text-xs text-red-300" data-testid="stat-mismatch-banner">
          <WarningIcon size={13} weight="fill" />
          <b>{stats.linked_mismatch}</b> slip(s) are linked to an SO that does <b>NOT</b> contain the box's Product Code — filter by status <span className="font-mono">Linked ·</span> to review.
          <button onClick={() => setStatusFilter("Linked · SO does NOT contain this Product Code")} className="ml-2 underline">Show</button>
        </div>
      )}

      {/* Filters + table */}
      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlassIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="ps-report-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search slip / product / customer / SO…"
              className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
          </div>
          <select data-testid="ps-report-status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className={`${inpCls} w-56`}>
            <option value="all">All statuses</option>
            <option>Already Linked</option>
            <option>Single Match</option>
            <option>Multiple Matches Found</option>
            <option>No Match Found</option>
            <option>Linked · SO does NOT contain this Product Code</option>
          </select>
          <span className="ml-auto text-xs text-zinc-500">{filtered.length} of {(data?.rows || []).length}</span>
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Slip No</th>
                <th className="px-3 py-2 text-left">Pack Date</th>
                <th className="px-3 py-2 text-left">Product Code</th>
                <th className="px-3 py-2 text-right">Boxes</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Current SO</th>
                <th className="px-3 py-2 text-left">Proposed SO No</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Matches</th>
                <th className="px-3 py-2 text-left">Apply</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="text-center py-8 text-zinc-500 text-xs">Scanning slips…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-zinc-500 text-xs">
                  {(data?.rows || []).length === 0 ? "No slips found in this range." : "No rows match the current filter."}
                </td></tr>
              )}
              {!loading && filtered.map((r, i) => (
                <tr key={r.slip_no} data-testid={`ps-report-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30 align-top">
                  <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs whitespace-nowrap">{r.slip_no}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-400">{(r.pack_date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-mono">{r.product_code || <span className="text-zinc-600 italic">no product code</span>}</div>
                    {r.all_product_codes.length > 1 && <div className="text-[10px] text-amber-300 mt-0.5">+{r.all_product_codes.length - 1} other PC(s) on this slip</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-xs text-zinc-300">{r.box_count}</td>
                  <td className="px-3 py-2 text-xs">{r.customer || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.current_so_no || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-3 py-2 text-xs font-mono">
                    {r.proposed_so_no
                      ? <span className={r.match_count > 1 ? "text-amber-300" : "text-emerald-300"}>{r.proposed_so_no}</span>
                      : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${statusCls(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-xs text-zinc-400">{r.match_count}</td>
                  <td className="px-3 py-2">
                    {/* Feb 2026 · Multi-Match Resolver · per-row SO chooser.
                        Single-Match rows come pre-selected; Multi-Match need
                        the user to pick; Already-Linked / No-Match are
                        intentionally locked to keep the UI honest. */}
                    {(r.status === "Single Match" || r.status === "Multiple Matches Found") ? (
                      <select
                        data-testid={`ps-pick-${i}`}
                        value={selections[r.slip_no] || ""}
                        onChange={e => setSelections(s => {
                          const next = { ...s };
                          if (e.target.value) next[r.slip_no] = e.target.value; else delete next[r.slip_no];
                          return next;
                        })}
                        className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)] min-w-[180px]"
                      >
                        <option value="">— skip —</option>
                        {r.candidates.map(c => (
                          <option key={c.id} value={c.id}>{c.so_no} · {(c.customer_name || "").slice(0, 20) || "no customer"}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-[10px] text-zinc-600 font-mono uppercase tracking-widest">{r.status === "Already Linked" ? "linked" : "n/a"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Audit log · surfaces every applied backfill so accidental relinks can be reversed */}
      {showLog && (
        <div className="tactical-card p-4 space-y-3" data-testid="ps-audit-log">
          <div className="flex items-center justify-between">
            <div className="overline">Backfill Audit Log · {auditLog.length}</div>
            <button onClick={loadLog} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
              <ArrowsClockwiseIcon size={11} weight="bold" />Refresh
            </button>
          </div>
          <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Decided At</th>
                  <th className="px-3 py-2 text-left">Slip No</th>
                  <th className="px-3 py-2 text-left">From order_id</th>
                  <th className="px-3 py-2 text-left">To SO No</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Decided By</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-xs">No backfills applied yet.</td></tr>
                )}
                {auditLog.map((l, i) => (
                  <tr key={l.id} className="border-t border-[var(--border)]" data-testid={`ps-audit-row-${i}`}>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-zinc-400">{(l.decided_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-[var(--accent)]">{l.slip_no}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-zinc-500">{(l.from_order_id || "—").slice(0, 8)}{l.from_order_id ? "…" : ""}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-emerald-300">{l.to_so_no}</td>
                    <td className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-400">{l.source}</td>
                    <td className="px-3 py-1.5 text-xs text-zinc-400">{l.decided_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
